import config from "@/../package.json"
import menu from "@/menu.json"
import {useWindowSize} from "@vueuse/core/index";
import type {ComputedRef} from "vue";
import {computed, provide, Ref, ref} from "vue";
import {useRoute} from "vue-router";
import {useI18n} from "vue-i18n";
import {useAuthorizationStore} from "@/stores/AuthorizationStore/AuthorizationStore";

export interface IChildLink {
    value: string
    to: string
    icon: string
    service?: string
}

export interface ILink extends IChildLink {
    childLinks?: Array<IChildLink>
}

export interface IMenuGroup {
    caption: boolean,
    role: 'USER' | 'ADMIN' | 'DEV' | 'APP_ADMIN'
    links: Array<ILink>
}

export interface IAppConst {
    LOCALE: string,
    USER_ROLE: number,
    ADMIN_ROLE: number,
    ROLES: Array<IAppConstRoleEntry>,
    SUPPORT_URL: string,
    VERSION: string,
    VERSION_HISTORY: Array<string>
    MENU: any,
    RIGHT_PANEL_WIDTH: string,
    showSidePanel: Ref<boolean>,
    sidePanelPageOffset: ComputedRef<string>,
    getTitle: (links: any, title: any) => string
    extractTitle: (title: any, link: any, getTitle: any) => string
    getIcon: (links: any, icon: any) => string
}

interface IAppConstRoleEntry {
    title: string,
    value: number,
}

/** Role constants – importable without calling the composable. */
export const USER_ROLE: number = 2
export const ADMIN_ROLE: number = 1
export const FRONTEND_ROLE: number = 3
export const APP_ADMIN_ROLE: number = 4

export const useAppConst = (): IAppConst => {
    const {width} = useWindowSize();
    const route = useRoute()
    const {t} = useI18n()
    const APP_CONST = {
        LOCALE: 'de-AT', // 'en-US'
        ROLES: [
            {title: 'USER', value: USER_ROLE},
            {title: 'ADMIN', value: ADMIN_ROLE},
            {title: 'APP_ADMIN', value: APP_ADMIN_ROLE},
        ],
        SUPPORT_URL: "https://kpmgatprod.service-now.com/sp?id=sc_cat_item&sys_id=b8b44669db005010739473e3f3961926",
        VERSION: config.version,
        VERSION_HISTORY: [],
        MENU: <Array<IMenuGroup>>menu,
        RIGHT_PANEL_WIDTH: '26rem'
    }

    const showSidePanel: Ref<boolean> = ref<boolean>(false)

    const sidePanelPageOffset: ComputedRef<string> = computed(() => {
        return showSidePanel.value && width.value > 1400 ? APP_CONST.RIGHT_PANEL_WIDTH : '0'
    })

    const extractTitle = (title: any, link: any, getTitle: any) => {
        if (title) {
            return title;
        } else if (link.childLinks) {
            const childTitle = getTitle(link.childLinks, title)
            return (childTitle)
                ? t('menu.' + link.value) + ' / ' + childTitle
                : title
        } else if (link.to && (route.path === link.to || route.path.startsWith(link.to + '/'))) {
            return t('menu.' + link.value)
        } else {
            return null
        }
    }
    const getIcon = (links: any, icon: any) => {
        return icon ?? links.reduce(
            (icon: any, link: any) => {
                if (icon) {
                    return icon
                } else if (link.childLinks) {
                    return getIcon(link.childLinks, icon)
                } else if (link.to && (route.path === link.to || route.path.startsWith(link.to + '/'))) {
                    return link.icon
                } else {
                    return null
                }
            }, null
        )
    }
    const getTitle = (links: any, title: any) => {
        return title ?? links.reduce((title: any, link: any) => extractTitle(title, link, getTitle), null)
    }


    provide('showSidePanel', showSidePanel)

    return {
        ...APP_CONST,
        USER_ROLE,
        ADMIN_ROLE,
        showSidePanel,
        sidePanelPageOffset,
        getTitle,
        extractTitle,
        getIcon
    }
}
